import {
  REQUEST_DEMO,
  USER_ATTRIBUTES,
  USER_BY_COMPANY,
  USER_BY_FB_PAGE,
  USER_PAP_AFFILIATE,
  USERS_BY_COMPANY,
  USERS_BY_FB_PAGE,
  USERS_MY,
} from '@mm/api-routes';
import { snakeCase } from 'lodash';
import { PapAffiliate } from '../interfaces/session';
import CompanyUsersModel, {
  INVERTED_USER_ROLES,
  USER_ROLES,
} from '../model/CompanyUsersModel';
import AuthenticatedController from './AuthenticatedController';

const mapBEtoFE = item => {
  return {
    ...item,
    role: INVERTED_USER_ROLES[item.role] || item.role,
  } as CompanyUsersModel;
};
const mapFEtoBE = (item: CompanyUsersModel) => {
  return {
    ...item,
    role: USER_ROLES[item.role],
    allowedFacebookPageIds: item.allowedFacebookPageIds
      ? item.allowedFacebookPageIds.join(',')
      : item.allowedFacebookPageIds,
  };
};
export default class UsersController extends AuthenticatedController {
  static async getUserById(companyId, userId) {
    const response = await super.get(USER_BY_COMPANY(companyId, userId));
    return mapBEtoFE(response);
  }

  static async getMyUser() {
    const response = await super.get(USERS_MY);
    return mapBEtoFE(response);
  }

  static async getAllByCompany(
    companyId,
    pageNumber = 1,
    pageSize = 25,
    query = '',
  ) {
    const queryOptions = query ? `&query=${query}` : '';
    const requestString = `${USERS_BY_COMPANY(
      companyId,
      pageNumber,
      pageSize,
    )}${queryOptions}`;

    const { users, pagination } = await super.get(requestString);
    return {
      entities: users.map(mapBEtoFE),
      pagination,
    };
  }

  static async getAllByFBPage(fbPageId, companyId, pageNumber, pageSize) {
    const response = await super.get(
      USERS_BY_FB_PAGE(fbPageId, companyId, pageNumber, pageSize),
    );
    return response.users.map(mapBEtoFE);
  }

  static async updateCompanyUser(companyId, userId, update) {
    const mappedEntity = mapFEtoBE(update);

    const response = await super.put(
      USER_BY_COMPANY(companyId, userId),
      mappedEntity,
    );
    return mapBEtoFE(response);
  }

  static async updateUserAttributes(entityId, userAttributes) {
    const arrayOfKeysAndValues = Object.entries(userAttributes);
    const hash = {};
    arrayOfKeysAndValues.forEach(arr => {
      hash[snakeCase(arr[0])] = arr[1];
    });
    const response = await super.put(USER_ATTRIBUTES(entityId), { ...hash });
    return mapBEtoFE(response);
  }

  static requestDemo(companyId) {
    return super.post(REQUEST_DEMO, { company_id: companyId });
  }

  static async updateFBPageUser(fbPageId, entity) {
    const mappedEntity = mapFEtoBE(entity);
    const response = await super.put(
      USER_BY_FB_PAGE(fbPageId, entity.id),
      mappedEntity,
    );
    return mapBEtoFE(response);
  }

  static async deleteCompanyUser(companyId, entityId) {
    return super.delete(USER_BY_COMPANY(companyId, entityId));
  }

  static async deleteFBPageUser(fbPageId, entityId) {
    return super.delete(USER_BY_FB_PAGE(fbPageId, entityId));
  }

  static async getUserPapAffiliate(userId): Promise<PapAffiliate> {
    return super.get(USER_PAP_AFFILIATE(userId));
  }

  static async putUserPapAffiliate(
    userId: number,
    params: {
      referralid: string;
      paypalEmail: string;
      username: string;
    },
  ): Promise<{
    success: boolean;
    papAffiliate?: PapAffiliate;
    errors?: string[];
  }> {
    try {
      return await super.put(USER_PAP_AFFILIATE(userId), params);
    } catch (err) {
      const errorResponse = await err.json();
      return errorResponse;
    }
  }
}
